import { CompanyType, DashboardStatsType, ServiceType, ServiceItemType } from "@/interfaces";
import { DEFAULT_CACHE_TIME } from "@/utils/constants";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { SortConfig } from "@/components/DataTable/types";
import api from "./api";
import { serializeFilterParams } from "./queryParams";

/**
 * Create company request payload
 */
export interface CreateCompanyRequest {
  companyName: string;
  domain: string;
  country?: string;
  timezone?: string;
  currency?: string;
  language?: string;
  dateFormat?: string;
  parentCompanyId?: string;
  businessUnitId?: string | null;
  adminUserIds?: string[];
  crmIntegration?: string;
  paymentGateway?: string;
  projectManagement?: string;
  invoicingAccounting?: string;
  legalDocument?: string;
  description?: string;
  primaryColor?: string;
  secondaryColor?: string;
  sidebarGradientEnabled?: boolean;
  sidebarGradientStart?: string | null;
  sidebarGradientEnd?: string | null;
  isDefault?: boolean;
  isOmsActive?: boolean;
  ioPrefix?: string | null;
  isCrmActive?: boolean;
  signatoryName?: string | null;
  signatoryTitle?: string | null;
  panNumber?: string | null;
  gstNumber?: string | null;
  cinNumber?: string | null;
}

/**
 * Update company request payload
 */
export interface UpdateCompanyRequest extends Partial<CreateCompanyRequest> {
  isActive?: boolean;
}

/**
 * Integration settings payload
 */
export interface IntegrationSettings {
  baseUrl: string;
  apiKey: string;
  password: string;
}

/**
 * Company statistics response
 */
export interface CompanyStats {
  totalCompanies: number;
  activeCompanies: number;
  totalUsers: number;
}

/**
 * Parameters for company list query
 */
export interface UseCompaniesParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  fields?: string;
  enabled?: boolean;
}

/**
 * Company list response from API
 */
export interface CompanyListResponse {
  data: CompanyType[];
  total: number;
  page: number;
  limit: number;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Query keys for company-related queries
 */
export const companyKeys = {
  all: ["companies"] as const,
  lists: () => [...companyKeys.all, "list"] as const,
  list: (filters: Partial<UseCompaniesParams>) => [...companyKeys.lists(), filters] as const,
  details: () => [...companyKeys.all, "detail"] as const,
  detail: (id: string) => [...companyKeys.details(), id] as const,
};

/**
 * Service Items Response from api
 */
export interface ServiceItemsResponse {
  data: ServiceItemType[];
  error: boolean;
}

export const useCompaniesListAll = (params?: UseCompaniesParams) => {
  return useQuery<CompanyListResponse>({
    queryKey: ["companies", "all", params],
    queryFn: async () => {
      const response = await api.get("/company", {
        params: {
          limit: params?.limit ?? 0,
          page: params?.page ?? 1,
          search: params?.search,
          fields: params?.fields,
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_CACHE_TIME,
    enabled: params?.enabled ?? true,
  });
};

/**
 * React Query hook for fetching a company by ID
 */
export const useGetCompanyById = (companyId: string | null) => {
  return useQuery<CompanyType>({
    queryKey: ["company", companyId],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID is required");
      const response = await api.get("/company/id", { params: { id: companyId } });
      return response.data.company;
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
    staleTime: 1000, // 1 second
  });
};

/**
 * React Query hook for fetching services assigned to a company
 */
export const useGetServicesByCompanyId = (companyId: string | null) => {
  return useQuery<{
    data: ServiceType[];
  }>({
    queryKey: ["company", companyId, "services"],
    queryFn: async () => {
      if (!companyId) throw new Error("Company ID is required");
      const response = await api.get(`/company/${companyId}/services`);
      return response.data;
    },
    enabled: !!companyId,
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * React Query hook for fetching SuperAdmin dashboard statistics
 * @deprecated Use useSuperAdminDashboard from dashboardService.ts
 */
export const useDashboardStats = () => {
  return useQuery<DashboardStatsType>({
    queryKey: ["dashboard", "stats"],
    queryFn: async () => {
      const response = await api.get("/dashboard/super-admin");
      return response.data.data;
    },
    staleTime: DEFAULT_CACHE_TIME,
  });
};

/**
 * Company service for API calls
 */
const companyService = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sort?: SortConfig;
    filters?: Record<string, unknown>;
    fields?: string;
    signal?: AbortSignal;
  }): Promise<CompanyListResponse> => {
    const { page = 1, limit = 20, search, sort, filters, fields, signal } = params || {};
    const serializedFilters = serializeFilterParams(filters);
    const response = await api.get<CompanyListResponse>("/company", {
      params: {
        page,
        limit,
        search,
        fields,
        sortField: sort?.field,
        sortOrder: sort?.order,
        ...serializedFilters,
      },
      signal,
    });
    return response.data;
  },

  /**
   * Create new company
   */
  create: async (data: Partial<CompanyType>) => {
    const resp = await api.post("/company/create", data);
    return resp.data;
  },

  /**
   * Update company
   */
  update: async (_id: string, data: UpdateCompanyRequest) => {
    const resp = await api.put(`/company/update`, { _id, ...data });
    return resp.data;
  },

  /**
   * Delete company
   */
  delete: async (id: string) => {
    const resp = await api.delete(`/company/delete?id=${id}`, { data: { id } });
    return resp.data;
  },
  /**
   * Get company statistics
   */
  getStats: async () => {
    const resp = await api.get("/company/stats");
    return resp.data;
  },

  /**
   * Toggle company active status
   */
  toggleStatus: async (id: string, isActive: boolean) => {
    const resp = await api.patch("/company/status", { _id: id, isActive });
    return resp.data;
  },

  /**
   * Upload company logo
   */
  uploadLogo: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append("logo", file);

    const resp = await api.post(`/company/${companyId}/upload-logo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data;
  },

  /**
   * Upload company branding logo
   */
  uploadBrandingLogo: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append("brandingLogo", file);

    const resp = await api.post(`/company/${companyId}/upload-branding-logo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data;
  },

  /**
   * Upload company favicon
   */
  uploadFavicon: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append("favicon", file);

    const resp = await api.post(`/company/${companyId}/upload-favicon`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data;
  },

  /**
   * Upload company signature image (used on generated PO/IO PDFs)
   */
  uploadSignature: async (companyId: string, file: File) => {
    const formData = new FormData();
    formData.append("signature", file);

    const resp = await api.post(`/company/${companyId}/upload-signature`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return resp.data;
  },

  /**
   * Save integration settings for a specific provider
   * @param companyId - Company ID
   * @param provider - Integration provider (teamwork, quickbooks, stripe)
   * @param settings - Integration credentials (baseUrl, apiKey, password)
   */
  saveIntegrationSettings: async (companyId: string, provider: string, settings: IntegrationSettings) => {
    const resp = await api.post(`/company/${companyId}/integration-settings`, {
      provider,
      ...settings,
    });
    return resp.data;
  },

  /**
   * Get company by domain
   */
  getByDomain: async (domain: string) => {
    const resp = await api.get("/company/domain", { params: { domain } });
    return resp.data;
  },
};

export default companyService;
